import { nanoid } from '@reduxjs/toolkit';
import { motion, useAnimation } from 'motion/react';
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { isInChromeExtension } from 'utils/proxyUtils';
import { screenshotStorage } from '../../../services/screenshotStorage';
import { useAppSelector } from '../../../store';
import { setAnimateScreenshot } from '../../../store/tasksSlice';
import { getFullPageScreenshotId, getPageThumbnail, getViewportScreenshotId, setLightboxImageUrl, setLightboxOpen } from '../../../store/uiSlice';

function AnimatedScreenshot() {
    const dispatch = useDispatch();
    const controls = useAnimation();
    const fadeControls = useAnimation();
    const siteThumbnail = useSelector(getPageThumbnail)
    const viewportId = useAppSelector(getViewportScreenshotId);
    const fullPageId = useAppSelector(getFullPageScreenshotId);
    const [screenshot, setScreenshot] = useState<string | null>()

    const handleImageShow = () => {
        if (screenshot && viewportId && isInChromeExtension()) {
            dispatch(setLightboxImageUrl(screenshot));
            dispatch(setLightboxOpen(true));
        }
    }

    useEffect(() => {
        const sequence = async () => {
            // Start the main animation to move and resize

            await Promise.all([
                controls.start({
                    left: 20,
                    top: 'calc(100vh - 153px)',
                    width: 200,
                    height: 133,
                    transition: {
                        duration: 0.55,
                        ease: "easeInOut",
                    },
                }),
                fadeControls.start({
                    opacity: 0,
                    transition: {
                        duration: 1,
                        ease: "easeInOut",
                    },
                }),
            ]);

            // Wait for remaining time
            await new Promise((resolve) => setTimeout(resolve, 2800));

            // Animate out to the left
            await controls.start({
                left: '-200px',
                transition: {
                    duration: 0.55,
                    ease: "easeInOut",
                },
            });
            dispatch(setAnimateScreenshot(false));
        };

        sequence();
    }, [controls, fadeControls, dispatch]);

    useEffect(() => {
        const fetchScreenshot = async () => {
            let screenshot: string | null = await screenshotStorage.getScreenshotBlobUrl(viewportId || '');
            if (screenshot && screenshot !== '') {
                setScreenshot(screenshot);
            } else if (siteThumbnail) {
                setScreenshot(siteThumbnail)
            } else if (fullPageId) {
                screenshot = await screenshotStorage.getScreenshotBlobUrl(fullPageId || '');
                if (screenshot) {
                    setScreenshot(screenshot);
                }
            }
        }
        fetchScreenshot();
    }, [viewportId, siteThumbnail, fullPageId]);

    return (
        <>
            <motion.div
                initial={{
                    left: 0,
                    top: 0,
                    height: '100vh',
                    width: '100vw',
                }}
                animate={controls}
                className="z-50 border-2 border-white fixed rounded-lg bg-background cursor-pointer pointer-events-auto aspect-video overflow-hidden shadow-lg"
                id={nanoid()}>
                <motion.div
                    initial={{
                        opacity: 1,
                    }}
                    animate={{
                        opacity: 1,
                    }}
                    className="bg-cover h-full w-full relative flex justify-center items-center rounded-md leading-tight m-0 p-0"
                    id={nanoid()}
                    onClick={handleImageShow}
                    style={{
                        backgroundImage: screenshot ? `url(${screenshot})` : `''`,
                    }}
                >
                    <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                    <span className="text-sm text-white absolute drop-shadow-md">
                        Screenshot Generated
                    </span>
                    {/* White overlay that fades out to reveal the screenshot */}
                    <motion.div
                        initial={{ opacity: 1 }}
                        animate={fadeControls}
                        className="absolute inset-0 bg-white rounded-md"
                    />
                </motion.div>
            </motion.div>
        </>
    );
}
export default AnimatedScreenshot;
